import type { ComponentChildren } from 'preact';
import { Check, AlertCircle } from 'lucide-preact';
import { cn } from '@/lib/cn';

export interface Step {
  id: string;
  title: ComponentChildren;
  description?: ComponentChildren;
  optional?: boolean;
  disabled?: boolean;
  /**
   * Validez explícita del paso, independiente de su posición vs `current`:
   *  - 'complete': todos los campos requeridos están — punto verde con check.
   *  - 'incomplete': falta algo requerido — punto rojo con alerta.
   *  - 'pending' (o ausente): aún no aplicable; se renderiza el estado
   *    derivado del índice (azul si activo, gris si futuro).
   * Cuando se setea, gana sobre la heurística por posición.
   */
  validity?: 'complete' | 'incomplete' | 'pending';
}

export interface StepperProps {
  steps: Step[];
  current: number;
  onStepClick?: (index: number) => void;
  orientation?: 'horizontal' | 'vertical';
  /**
   * Variant compacto: oculta descriptions y muestra sólo el dot numerado más
   * el título del step activo. Pensado para wizards densos (8+ steps) en los
   * que el set completo de descriptions desordena el header.
   */
  compact?: boolean;
  /**
   * Estilo visual. `'stepper'` (default) renderiza el clásico dot numerado
   * con línea conectora — apto para flujos lineales (onboarding). `'tabs'`
   * renderiza una barra de pestañas con underline para el activo y un
   * indicador inline de validez (check verde / alerta roja) — pensado para
   * §8.1 reunión 2026-05-11: edición libre del setup tras el primer
   * onboarding. Las dos variantes comparten la misma API (steps + validity
   * + onStepClick), así el wizard puede alternarlas sin reescribir nada.
   */
  variant?: 'stepper' | 'tabs';
  /**
   * Si true, cualquier step no-`disabled` es clicable (no solo
   * completados/activo). Pensado para modo "editar" donde el usuario ya
   * tiene un setup y quiere saltar libremente. Default: false (linealidad).
   */
  allowFreeNavigation?: boolean;
  className?: string;
}

export function Stepper({
  steps,
  current,
  onStepClick,
  orientation = 'horizontal',
  compact = false,
  variant = 'stepper',
  allowFreeNavigation = false,
  className,
}: StepperProps) {
  const horizontal = orientation === 'horizontal';

  if (variant === 'tabs') {
    return (
      <div
        role="tablist"
        aria-orientation={horizontal ? 'horizontal' : 'vertical'}
        className={cn(
          horizontal
            ? // Wrap en vez de scroll horizontal: con 7-8 pasos la barra se
              // salia del contenedor y el ultimo titulo quedaba cortado.
              'border-border-subtle flex flex-wrap items-center gap-x-1 border-b'
            : 'flex flex-col gap-1',
          className,
        )}
      >
        {steps.map((step, i) => {
          const positionalCompleted = i < current;
          const active = i === current;
          const validityStatus = step.validity;
          const isValid = validityStatus === 'complete';
          const isInvalid = validityStatus === 'incomplete';
          const completed = isValid || (validityStatus === undefined && positionalCompleted);
          const clickable =
            !!onStepClick && !step.disabled && (allowFreeNavigation || completed || active);
          return (
            <button
              key={step.id}
              type="button"
              role="tab"
              aria-selected={active}
              aria-current={active ? 'page' : undefined}
              aria-invalid={isInvalid || undefined}
              disabled={!clickable}
              onClick={() => clickable && onStepClick?.(i)}
              className={cn(
                'group text-13 duration-med relative flex shrink-0 items-center gap-2 px-3 py-2 font-medium transition-colors',
                horizontal
                  ? '-mb-px border-b-2 border-transparent'
                  : 'border-l-2 border-transparent',
                active && 'border-brand text-fg-strong',
                !active && completed && 'text-fg hover:text-fg-strong',
                !active && !completed && !isInvalid && 'text-fg-muted',
                !active && isInvalid && 'text-error',
                clickable && !active && 'hover:border-border-strong cursor-pointer',
                !clickable && 'cursor-not-allowed opacity-60',
              )}
            >
              <span
                className={cn(
                  'text-3xs flex h-4 w-4 shrink-0 items-center justify-center rounded-full font-semibold',
                  isInvalid && 'bg-error-bg text-error',
                  !isInvalid && completed && 'bg-brand text-brand-contrast',
                  !isInvalid && !completed && active && 'bg-brand-tint text-brand',
                  !isInvalid && !completed && !active && 'bg-surface-2 text-fg-faint',
                )}
                aria-hidden="true"
              >
                {isInvalid ? <AlertCircle size={10} /> : completed ? <Check size={10} /> : i + 1}
              </span>
              <span className="whitespace-nowrap">{step.title}</span>
              {step.optional ? (
                <span className="text-3xs text-fg-faint tracking-wide uppercase">Opc.</span>
              ) : null}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <ol className={cn(horizontal ? 'flex items-center gap-2' : 'flex flex-col gap-3', className)}>
      {steps.map((step, i) => {
        const positionalCompleted = i < current;
        const active = i === current;
        // `validity` (si se pasa) gana sobre la heurística por posición. El
        // wizard pasa 'complete' para steps cuyos campos requeridos están
        // todos rellenos e 'incomplete' para los que aún les falta algo.
        const validityStatus = step.validity;
        const isValid = validityStatus === 'complete';
        const isInvalid = validityStatus === 'incomplete';
        const completed = isValid || (validityStatus === undefined && positionalCompleted);
        const clickable =
          !!onStepClick && !step.disabled && (allowFreeNavigation || completed || active);
        const status: 'completed' | 'active' | 'pending' | 'invalid' = isInvalid
          ? 'invalid'
          : completed
            ? 'completed'
            : active
              ? 'active'
              : 'pending';
        const showLabel = compact ? active : true;
        return (
          <li
            key={step.id}
            className={cn(
              'flex items-center gap-2',
              compact ? 'shrink-0' : horizontal ? 'flex-1 items-start' : '',
            )}
            aria-current={active ? 'step' : undefined}
          >
            <button
              type="button"
              disabled={!clickable}
              onClick={() => clickable && onStepClick?.(i)}
              className={cn(
                'duration-med flex shrink-0 items-center justify-center rounded-full border font-semibold transition-colors',
                compact ? 'text-2xs h-6 w-6' : 'h-7 w-7 text-xs',
                status === 'completed' && 'border-brand bg-brand text-brand-contrast',
                status === 'active' && 'border-brand bg-brand-tint text-brand',
                status === 'pending' && 'border-border-strong bg-surface text-fg-faint',
                status === 'invalid' && 'border-error bg-error-bg text-error',
                clickable && 'hover:border-brand cursor-pointer',
                !clickable && 'cursor-not-allowed',
              )}
              aria-label={`Paso ${i + 1}: ${typeof step.title === 'string' ? step.title : ''}`}
              aria-invalid={isInvalid || undefined}
            >
              {status === 'completed' ? (
                <Check size={compact ? 12 : 14} />
              ) : status === 'invalid' ? (
                <AlertCircle size={compact ? 12 : 14} />
              ) : (
                i + 1
              )}
            </button>
            {showLabel ? (
              <div className={cn('flex min-w-0 flex-col', compact ? '' : 'pt-0.5 leading-tight')}>
                <span
                  className={cn(
                    compact ? 'text-13 truncate' : 'text-13',
                    'font-medium',
                    active ? 'text-fg-strong' : completed ? 'text-fg' : 'text-fg-muted',
                  )}
                >
                  {step.title}
                </span>
                {!compact && step.description ? (
                  <span className="text-2xs text-fg-muted">{step.description}</span>
                ) : null}
                {!compact && step.optional ? (
                  <span className="text-3xs text-fg-faint tracking-wide uppercase">Opcional</span>
                ) : null}
              </div>
            ) : null}
            {horizontal && i < steps.length - 1 ? (
              <span
                className={cn(
                  'h-px flex-1',
                  compact ? 'mx-1 min-w-[16px]' : 'mx-2 mt-3.5',
                  i < current ? 'bg-brand' : 'bg-border-subtle',
                )}
                aria-hidden="true"
              />
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}
